<template>
  <BaseWidget
    :title="widgetTitle"
    :disable-date-range="true"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    :row-span="rowSpan"
    :col-span="colSpan"
    :show-info-icon="true"
    info-tooltip="Total credit balance owed to users from invoices with a negative balance, across all time"
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/3469318-how-does-credit-work-in-optix', text: 'Learn about Credit' }"
    :overflow-menu-items="taxMenuItems"
    @remove-widget="$emit('remove-widget', props.widgetId)"
    @overflow-select="handleOverflowSelect"
  >
    <div
      v-if="loading"
      class="widget-content-wrapper"
    >
      <SkeletonLoader
        type="widget"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
    >
      <MetricValue
        :value="creditOwedFormatted"
        :subtitle="subtitleText"
      />
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'
import MetricValue from './MetricValue.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useCreditOwedData } from '../composables/useWidgetData.js'
import { isRateLimitError } from '../utils/errorUtils.js'

const props = defineProps({
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  editMode: {
    type: Boolean,
    default: false
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

const excludeTax = ref(props.props?.excludeTax || false)

const widgetTitle = 'Credit owed'

const taxMenuItems = computed(() => [
  { label: 'Exclude tax', value: 'exclude-tax', active: excludeTax.value },
  { label: 'Include tax', value: 'include-tax', active: !excludeTax.value }
])

// Keep the excl-tax state in the subtitle so the title can't wrap and hide labels (SDWEB-18441).
const subtitleText = computed(() => (excludeTax.value ? 'All time · Excl. tax' : 'All time'))

const { data, loading, error, cleanup, refresh } = useCreditOwedData({
  variables: computed(() => ({ excludeTax: excludeTax.value })),
  priority: 1
})

const creditOwedAmount = computed(() => {
  return data.value?.creditOwedTotal || 0
})

const formatCurrency = (amount) => {
  const currencySymbol = data.value?.currency_symbol || '$'
  const locale = (data.value?.locale || 'en-US').replace(/_/g, '-')

  if (amount === 0) return `${currencySymbol}0`

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: data.value?.currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  } catch (e) {
    return `${currencySymbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
}

const creditOwedFormatted = computed(() => {
  return formatCurrency(creditOwedAmount.value)
})

const handleOverflowSelect = (value) => {
  const newExcludeTax = value === 'exclude-tax'
  if (newExcludeTax !== excludeTax.value) {
    excludeTax.value = newExcludeTax
    emit('update:props', { excludeTax: newExcludeTax })
  }
}

watch(() => props.props?.excludeTax, (newVal) => {
  if (newVal !== undefined && newVal !== excludeTax.value) {
    excludeTax.value = newVal
  }
})

watch(excludeTax, () => {
  refresh(true, { showLoading: true })
})

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;
  min-height: 0;
  overflow: hidden;
}


.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}
</style>
